/** Lokale UI-Vorgaben (Darkmode, Schriftgrad) — document.documentElement */

export type LrThemePreference = 'light' | 'dark' | 'system'
export type LrTextScale = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const STORAGE_THEME = 'lr-ui-theme'
const STORAGE_TEXT = 'lr-ui-text-scale'

const TEXT_SCALE_VALUES: LrTextScale[] = ['xs', 'sm', 'md', 'lg', 'xl']

export function canDecreaseTextScale(scale: LrTextScale): boolean {
  return TEXT_SCALE_VALUES.indexOf(scale) > 0
}

export function canIncreaseTextScale(scale: LrTextScale): boolean {
  const index = TEXT_SCALE_VALUES.indexOf(scale)
  return index >= 0 && index < TEXT_SCALE_VALUES.length - 1
}

export function decreaseTextScale(scale: LrTextScale): LrTextScale {
  const index = TEXT_SCALE_VALUES.indexOf(scale)
  if (index <= 0) return TEXT_SCALE_VALUES[0]
  return TEXT_SCALE_VALUES[index - 1]
}

export function increaseTextScale(scale: LrTextScale): LrTextScale {
  const index = TEXT_SCALE_VALUES.indexOf(scale)
  if (index < 0 || index >= TEXT_SCALE_VALUES.length - 1) {
    return TEXT_SCALE_VALUES[TEXT_SCALE_VALUES.length - 1]
  }
  return TEXT_SCALE_VALUES[index + 1]
}

export function getThemePreference(): LrThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_THEME)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch {
    /* ignore */
  }
  return 'system'
}

export function getTextScale(): LrTextScale {
  try {
    const v = localStorage.getItem(STORAGE_TEXT)
    if (TEXT_SCALE_VALUES.includes(v as LrTextScale)) return v as LrTextScale
  } catch {
    /* ignore */
  }
  return 'md'
}

export function isDarkResolved(theme: LrThemePreference): boolean {
  if (theme === 'dark') return true
  if (theme === 'light') return false
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

/** Wendet Theme + Text-Skala auf `<html>` an (Klassen `dark`, `text-scale-*`). */
export function applyUiPreferencesToDocument(theme?: LrThemePreference, textScale?: LrTextScale): void {
  if (typeof document === 'undefined') return
  const t = theme ?? getThemePreference()
  const s = textScale ?? getTextScale()
  const root = document.documentElement
  root.classList.toggle('dark', isDarkResolved(t))
  for (const step of TEXT_SCALE_VALUES) {
    root.classList.remove(`text-scale-${step}`)
  }
  root.classList.add(`text-scale-${s}`)
}

export function setThemePreference(pref: LrThemePreference): void {
  try {
    localStorage.setItem(STORAGE_THEME, pref)
  } catch {
    /* ignore */
  }
  applyUiPreferencesToDocument(pref, undefined)
}

export function setTextScale(pref: LrTextScale): void {
  try {
    localStorage.setItem(STORAGE_TEXT, pref)
  } catch {
    /* ignore */
  }
  applyUiPreferencesToDocument(undefined, pref)
}

export function subscribeSystemTheme(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const mq = window.matchMedia('(prefers-color-scheme: dark)')
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}
